import React, { useCallback } from 'react';
import { useSelector } from 'react-redux';
import { submitMSISDNAction, usePinFlowState } from 'ouisys-engine/pinFlow';
import { defineMessages, Link } from 'ouisys-component-library';
import { unblockUser } from 'ouisys-engine/utilities/unblockUser';
import { useDispatch } from '../../__doNotModify/store';
import { useRootContext } from '../../providers/RootContext';
import { injectIntl, FormattedMessage } from '../../localization/index';
import peruErrors from './peru-errors.json';
import './ErrorModal.scss';

// error messages messages which might come from backend
const defaultMessages = defineMessages({
  UnexpectedState: {
    id: 'UnexpectedState',
    defaultMessage: 'Unexpected state error. Please try again later.'
  }
});

const processErrorMessage = (message: string) => {
  return message.includes(',')
    ? message.split(',')[1].trim()
    : message.trim();
};

const Modal = ({ error }: any) => {
  const dispatch = useDispatch();
  const pinFlowState = usePinFlowState(useSelector);
  const pinFlowData = pinFlowState?.data;
  const { msisdn, showModal, setShowModal } = useRootContext();

  // error response handler
  const errorMessage = error.message !== undefined && error.message === 'ALREADY SUBSCRIBED'
    ? 'ALREADY_SUBSCRIBED'
    : error.message !== undefined && error.message.toLowerCase().includes('user account is blacklisted')
      ? 'HOLDER_BLACKLISTED'
      : error.message !== undefined && error.message.includes('EMPELLO')
        ? 'EMPELLO_SUSPICIOUS_ACTIVITY'
        : processErrorMessage(error.message !== undefined ? error.message : '');

  const closeModalHandler = useCallback(() => {
    // close the modal
    setShowModal(false);

    // grab existing url params
    const currentUrl = new URL((window as any).location);

    // set the msisdn and rockman_id to url params
    currentUrl.searchParams.set('msisdn', msisdn);
    currentUrl.searchParams.set('rockman_id', window.pac_analytics.visitor.rockmanId);

    // add show-pin-entry on url params if not exist
    const hasShowPINEntryParams = currentUrl.searchParams.get('show-pin-entry') === '1';
    if (!hasShowPINEntryParams && pinFlowData !== undefined) {
      currentUrl.searchParams.set('show-pin-entry', '1');
    }

    // reload the page to renew Empello token on modal close
    window.location.href = currentUrl.toString();
  }, [msisdn, window.pac_analytics.visitor.rockmanId, pinFlowData]);

  const buttonClickHandler = useCallback((event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    // prevent default button behavior
    event.preventDefault();

    // call unblock api
    if (peruErrors[errorMessage].action === 'request_unblock') {
      unblockUser({
        window,
        msisdn,
        ...(pinFlowData !== undefined ? { extraParams: { "show-pin-entry": "1" }} : {})
      });
    }

    // trigger msisdn submission
    if (peruErrors[errorMessage].action === 'request_pin') {
      dispatch(submitMSISDNAction({ window, msisdn }));
    }

    // redirect for already subscribed user
    if (peruErrors[errorMessage].action === 'request_redirect') {
      window.location.href = error.productUrl;
    }

    closeModalHandler();
  }, [peruErrors[errorMessage]]);

  return (
    <div className={`Modal ${showModal ? 'is-visible' : ''}`}>
      <div className='Modal__content'>
        <div className="Modal__body">
          {
            error.message !== undefined && peruErrors[errorMessage]
            && (
              <>
                {peruErrors[errorMessage].title && (
                  <div className="Modal__body-title">
                    {peruErrors[errorMessage].title}
                  </div>
                )}
                {peruErrors[errorMessage].text && (
                  <div className="Modal__body-text">
                    {peruErrors[errorMessage].text}
                  </div>
                )}
                {peruErrors[errorMessage].button_text && (
                  <button className="btn" onClick={buttonClickHandler}>
                    {peruErrors[errorMessage].button_text}
                  </button>
                )}
              </>
            )
            || (
              <>
                <div className="Modal__close" onClick={closeModalHandler}>✖</div>
                <div className="Modal__body-text">
                  <FormattedMessage id={error.errorType || ''} />
                </div>
                {error.errorType == 'AlreadySubscribed' && (
                  <Link className="btn" target="_blank" href={error.productUrl}>
                    <FormattedMessage id="GoToProduct" defaultMessage="Go to Product" />
                  </Link>
                )}
              </>
            )
          }
        </div>
      </div>
    </div>
  );
}

export default injectIntl(Modal);